import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Globe, Smartphone, Search, BarChart3, CheckCircle, ArrowRight, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { siteConfig } from '@/config/site'
import { SEO } from '@/components/SEO'
import { FAQSchema } from '@/components/StructuredData'

const iconMap: Record<string, LucideIcon> = {
  Globe,
  Smartphone,
  Search,
  BarChart3,
}

const faqs = [
  {
    question: 'How much does a website cost?',
    answer: 'Our websites start from £499 for a simple business website. Online shops and more complex sites are quoted individually based on your needs. We always give you a fixed price upfront with no hidden fees.',
  },
  {
    question: 'How long does it take to build a website?',
    answer: 'A simple business website typically takes 2-3 weeks. Larger projects like online shops or mobile apps take 4-8 weeks. We will give you a clear timeline before we start.',
  },
  {
    question: 'Do I need to pay monthly?',
    answer: 'Website builds are a one-time fee. Ongoing services like SEO and social media management are monthly. We are flexible and can work with your budget.',
  },
  {
    question: 'Can I update my website myself?',
    answer: 'Yes! We build websites that are easy to update. We will show you how to make changes yourself, and we are always here if you need help.',
  },
  {
    question: 'What if I am not happy with the result?',
    answer: 'Your satisfaction is guaranteed. We work closely with you throughout the project, and we will keep making changes until you love it.',
  },
]

export function Services() {
  return (
    <>
      <SEO
        title="Our Services - Websites, Apps, SEO & Marketing"
        description="Professional web design from £499, mobile apps from £1,499, SEO from £199/month, and social media marketing for UK small businesses. View our services and pricing."
        keywords={[
          'web design services UK',
          'mobile app development',
          'SEO services pricing',
          'social media marketing packages',
          'small business website cost',
          'affordable web design UK',
          'e-commerce website development',
        ]}
        url="https://nexaigen.co.uk/services"
      />
      <FAQSchema faqs={faqs} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="pt-32 pb-20 container bg-background min-h-screen"
      >
        {/* Header */}
      <div className="max-w-4xl mx-auto mb-20 text-center">
        <h1 className="text-5xl md:text-7xl font-bold mb-8 text-white tracking-tight">
          What We <span className="text-primary">Do</span>
        </h1>
        <p className="text-xl text-slate-400">
          Simple, effective solutions to help your business grow online.
          No confusing packages. Just what you need.
        </p>
      </div>

      {/* Services Grid */}
      <div className="max-w-6xl mx-auto space-y-8">
        {siteConfig.services.map((service, index) => {
          const Icon = iconMap[service.icon] || Globe
          return (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8 md:p-12 rounded-3xl bg-slate-900/50 border border-white/5 hover:border-primary/20 transition-colors"
            >
              {/* Left: Info */}
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                    <Icon size={28} />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-white">{service.name}</h2>
                    <p className="text-primary font-bold">{service.price}</p>
                  </div>
                </div>

                <p className="text-slate-400 text-lg leading-relaxed">
                  {service.description}
                </p>

                <Link to="/contact">
                  <Button className="mt-4">
                    Get Started <ArrowRight size={16} />
                  </Button>
                </Link>
              </div>

              {/* Right: Features */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-white mb-4">What You Get:</h3>
                {service.features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-accent mt-0.5 shrink-0" />
                    <span className="text-slate-300">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )
        })}
      </div>

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto mt-32">
        <h2 className="text-4xl font-bold text-white text-center mb-12">
          Common <span className="text-primary">Questions</span>
        </h2>

        <div className="space-y-6">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 rounded-2xl bg-slate-900/50 border border-white/5"
            >
              <h3 className="text-xl font-bold text-white mb-3">{faq.question}</h3>
              <p className="text-slate-400">{faq.answer}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-2xl mx-auto mt-20 text-center">
        <h2 className="text-3xl font-bold text-white mb-4">
          Not Sure What You Need?
        </h2>
        <p className="text-slate-400 mb-8">
          No problem! Book a free chat and we will help you figure out the best solution for your business.
        </p>
        <Link to="/contact">
          <Button size="lg">
            Book a Free Chat <ArrowRight size={18} />
          </Button>
        </Link>
      </div>
      </motion.div>
    </>
  )
}

export default Services
